import { useEffect } from 'react';
import Navbar from '../components/Navbar';
import { Shield, FileText, Scale, AlertTriangle, CreditCard, Truck, Package, Users, Globe, Lock, Mail, Phone } from 'lucide-react';

function Terms() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const lastUpdated = "April 9, 2026";
  const companyName = "BlueCrate Group LLC";
  const companyAddress = "8 The Green STE B, Dover, DE 19901";
  const companyEmail = "legal@bluecrategroup.com";
  const companyPhone = "(555) 123-4567";
  const websiteUrl = "bluecrategroup.com";

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-gray-50">
      <Navbar />
      
      <div className="pt-32 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-primary/10 rounded-full mb-6">
              <Scale className="w-8 h-8 text-primary" />
            </div>
            <h1 className="text-4xl font-bold mb-4">Terms of Service</h1>
            <p className="text-gray-600">Last Updated: {lastUpdated}</p>
          </div>

          {/* Content */}
          <div className="bg-white rounded-3xl shadow-xl p-8 md:p-12 border border-gray-100">
            <div className="prose prose-lg max-w-none">
              
              {/* Important Notice */}
              <div className="bg-amber-50 border-2 border-amber-200 rounded-2xl p-6 mb-8">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-6 h-6 text-amber-600 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-bold text-amber-900 mt-0 mb-2">IMPORTANT LEGAL NOTICE</h3>
                    <p className="text-amber-800 text-sm mb-0">
                      PLEASE READ THESE TERMS OF SERVICE CAREFULLY BEFORE USING OUR WEBSITE OR PLACING AN ORDER. 
                      BY ACCESSING THIS WEBSITE, CREATING AN ACCOUNT, OR PLACING AN ORDER, YOU AGREE TO BE BOUND BY 
                      THESE TERMS AND ALL APPLICABLE LAWS AND REGULATIONS. IF YOU DO NOT AGREE WITH ANY OF THESE TERMS, 
                      YOU ARE PROHIBITED FROM USING THIS SITE OR PLACING ORDERS.
                    </p>
                  </div>
                </div>
              </div>

              {/* Section 1 */}
              <section className="mb-10">
                <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-3 mb-4">
                  <span className="w-8 h-8 bg-primary text-white rounded-full flex items-center justify-center text-sm font-bold">1</span>
                  Agreement to Terms
                </h2>
                <p>
                  These Terms of Service ("Terms," "Agreement") constitute a legally binding agreement between you ("Customer," 
                  "User," "you," or "your") and {companyName} ("Company," "we," "us," or "our"), governing your access to and 
                  use of the {websiteUrl} website, mobile applications, and all related services (collectively, the "Services").
                </p>
                <p>
                  By accessing our website, creating an account, browsing our products, or placing an order, you acknowledge 
                  that you have read, understood, and agree to be bound by these Terms, our Payment Policy, and our Privacy 
                  Policy, all of which are incorporated herein by reference. This Agreement is effective as of the date you 
                  first access the Services.
                </p>
                <p>
                  <strong>YOU MUST BE AT LEAST 18 YEARS OF AGE AND CAPABLE OF ENTERING INTO A LEGALLY BINDING CONTRACT TO USE 
                  OUR SERVICES.</strong> By using our Services, you represent and warrant that you meet these requirements.
                </p>
                <p>
                  If you are entering into this Agreement on behalf of a business, organization, or other legal entity, you 
                  represent and warrant that you have the authority to bind such entity to these Terms.
                </p>
              </section>

              {/* Section 2 */}
              <section className="mb-10">
                <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-3 mb-4">
                  <span className="w-8 h-8 bg-primary text-white rounded-full flex items-center justify-center text-sm font-bold">2</span>
                  Description of Services
                </h2>
                <p>
                  {companyName} operates a cooperative wholesale flooring purchasing platform that enables customers to 
                  collectively purchase building materials, including but not limited to luxury vinyl plank (LVP), 
                  engineered hardwood flooring, and related products, directly from overseas manufacturers at wholesale prices.
                </p>
                <h3 className="text-xl font-semibold mt-6 mb-3">2.1 Cooperative Container Model</h3>
                <p>
                  Our business model is based on shared container shipping. Multiple customers' orders are combined into 
                  a single shipping container to achieve significant cost savings. By placing an order, you understand and 
                  agree that:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Your order will be grouped with other customers' orders in a shared container</li>
                  <li>Production begins when a container reaches 80% capacity threshold</li>
                  <li>Shipping timelines depend on container fill rates and are estimates only</li>
                  <li>You do not have exclusive rights to container space until payment is complete</li>
                  <li>Container allocation is at our sole discretion based on factory location and logistics</li>
                </ul>
                <h3 className="text-xl font-semibold mt-6 mb-3">2.2 Product Information</h3>
                <p>
                  We strive to provide accurate product descriptions, specifications, images, and pricing. However, we do 
                  not warrant that product descriptions, images, pricing, or other content is accurate, complete, reliable, 
                  current, or error-free. Colors displayed on your screen may vary from actual product colors due to monitor 
                  settings and lighting conditions. Product specifications are subject to manufacturing tolerances.
                </p>
              </section>

              {/* Section 3 */}
              <section className="mb-10">
                <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-3 mb-4">
                  <span className="w-8 h-8 bg-primary text-white rounded-full flex items-center justify-center text-sm font-bold">3</span>
                  User Accounts
                </h2>
                <p>
                  To place orders and access certain features, you must create an account. You agree to:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Provide accurate, current, and complete information during registration</li>
                  <li>Maintain and promptly update your account information</li>
                  <li>Maintain the security of your password and account</li>
                  <li>Accept responsibility for all activities under your account</li>
                  <li>Immediately notify us of any unauthorized use of your account</li>
                </ul>
                <p className="mt-4">
                  We reserve the right to suspend, terminate, or refuse service to any account at our sole discretion, 
                  including but not limited to accounts associated with fraudulent activity, violation of these Terms, 
                  or abusive behavior toward our staff.
                </p>
              </section>

              {/* Section 4 */}
              <section className="mb-10">
                <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-3 mb-4">
                  <span className="w-8 h-8 bg-primary text-white rounded-full flex items-center justify-center text-sm font-bold">4</span>
                  Orders and Contracts
                </h2>
                <h3 className="text-xl font-semibold mt-6 mb-3">4.1 Order Placement</h3>
                <p>
                  By placing an order, you are making an offer to purchase products subject to these Terms. All orders 
                  are subject to acceptance by us. We reserve the right to refuse or cancel any order for any reason, 
                  including but not limited to:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Product unavailability</li>
                  <li>Errors in product or pricing information</li>
                  <li>Suspected fraudulent or unauthorized transactions</li>
                  <li>Orders that violate our policies or these Terms</li>
                  <li>Credit or payment verification failures</li>
                </ul>
                <h3 className="text-xl font-semibold mt-6 mb-3">4.2 Order Confirmation</h3>
                <p>
                  Your receipt of an order confirmation does not constitute our acceptance of your order. We reserve 
                  the right to cancel any order after confirmation if we determine, in our sole discretion, that the 
                  order should not be fulfilled.
                </p>
                <h3 className="text-xl font-semibold mt-6 mb-3">4.3 Binding Agreement</h3>
                <p>
                  <strong>UPON SUCCESSFUL PAYMENT OF YOUR DEPOSIT, A BINDING PURCHASE AGREEMENT IS FORMED.</strong> You 
                  are obligated to pay the remaining balance upon production completion. Failure to pay the remaining 
                  balance may result in forfeiture of your deposit, collection actions, and reporting to credit agencies.
                </p>
                <h3 className="text-xl font-semibold mt-6 mb-3">4.4 Minimum Order Requirements</h3>
                <p>
                  Products are sold in full box quantities with minimum order requirements as specified on each product 
                  listing. Orders below the minimum threshold cannot be processed.
                </p>
              </section>

              {/* Section 5 */}
              <section className="mb-10">
                <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-3 mb-4">
                  <span className="w-8 h-8 bg-primary text-white rounded-full flex items-center justify-center text-sm font-bold">5</span>
                  Pricing and Payment
                </h2>
                <p>
                  Please refer to our <a href="/payment-policy" className="text-primary hover:underline font-semibold">Payment Policy</a> for 
                  complete details regarding pricing, payment terms, deposits, refunds, and cancellations. The Payment 
                  Policy is incorporated into these Terms by reference.
                </p>
                <h3 className="text-xl font-semibold mt-6 mb-3">5.1 Pricing</h3>
                <p>
                  All prices are in U.S. Dollars (USD) and are subject to change without notice. Prices do not include 
                  applicable taxes, shipping costs, or insurance unless explicitly stated. We reserve the right to 
                  correct pricing errors even after an order has been placed or confirmed.
                </p>
                <h3 className="text-xl font-semibold mt-6 mb-3">5.2 Taxes</h3>
                <p>
                  You are responsible for all applicable sales taxes, use taxes, duties, and other governmental charges. 
                  Tax-exempt customers must provide valid documentation within 72 hours of order placement.
                </p>
                <h3 className="text-xl font-semibold mt-6 mb-3">5.3 Payment Security</h3>
                <p>
                  All payment information is encrypted and processed through secure, PCI-DSS compliant payment processors. 
                  We do not store your complete credit card information on our servers.
                </p>
              </section>

              {/* Section 6 */}
              <section className="mb-10">
                <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-3 mb-4">
                  <span className="w-8 h-8 bg-primary text-white rounded-full flex items-center justify-center text-sm font-bold">6</span>
                  Shipping and Delivery
                </h2>
                <h3 className="text-xl font-semibold mt-6 mb-3">6.1 Shipping Timelines</h3>
                <p>
                  <strong>ALL SHIPPING AND DELIVERY DATES ARE ESTIMATES ONLY AND ARE NOT GUARANTEED.</strong> Shipping 
                  timelines depend on various factors including but not limited to:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Container fill rate and production scheduling</li>
                  <li>Manufacturing lead times</li>
                  <li>International shipping conditions</li>
                  <li>Customs clearance processes</li>
                  <li>Domestic freight availability</li>
                  <li>Weather and force majeure events</li>
                </ul>
                <h3 className="text-xl font-semibold mt-6 mb-3">6.2 Delivery</h3>
                <p>
                  Delivery is to the curbside of the shipping address provided. You are responsible for unloading, 
                  inspection, and moving materials from the delivery point. Special delivery requirements (liftgate, 
                  inside delivery, etc.) must be arranged in advance and may incur additional fees.
                </p>
                <h3 className="text-xl font-semibold mt-6 mb-3">6.3 Inspection and Acceptance</h3>
                <p>
                  <strong>YOU MUST INSPECT ALL PRODUCTS UPON DELIVERY.</strong> Any visible damage, shortages, or 
                  discrepancies must be noted on the delivery receipt AND reported to us within 48 hours of delivery. 
                  Failure to report issues within this timeframe constitutes acceptance of the products as delivered.
                </p>
                <h3 className="text-xl font-semibold mt-6 mb-3">6.4 Risk of Loss</h3>
                <p>
                  Title and risk of loss pass to you upon delivery to the carrier at the port of origin. We recommend 
                  purchasing shipping insurance to protect your investment.
                </p>
              </section>

              {/* Section 7 */}
              <section className="mb-10">
                <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-3 mb-4">
                  <span className="w-8 h-8 bg-primary text-white rounded-full flex items-center justify-center text-sm font-bold">7</span>
                  Cancellations and Refunds
                </h2>
                <p>
                  Please refer to our <a href="/payment-policy" className="text-primary hover:underline font-semibold">Payment Policy</a> for 
                  complete cancellation and refund terms. Key provisions include:
                </p>
                <div className="bg-gray-50 rounded-xl p-6 mt-4">
                  <ul className="space-y-3">
                    <li className="flex items-start gap-3">
                      <span className="w-2 h-2 bg-primary rounded-full mt-2"></span>
                      <span><strong>Before Production:</strong> Cancellation allowed with refund minus 10% processing fee</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="w-2 h-2 bg-primary rounded-full mt-2"></span>
                      <span><strong>After Production Begins:</strong> Deposit is non-refundable and forfeited</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="w-2 h-2 bg-primary rounded-full mt-2"></span>
                      <span><strong>After Production Complete:</strong> No cancellation permitted; full payment required</span>
                    </li>
                  </ul>
                </div>
              </section>

              {/* Section 8 */}
              <section className="mb-10">
                <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-3 mb-4">
                  <span className="w-8 h-8 bg-primary text-white rounded-full flex items-center justify-center text-sm font-bold">8</span>
                  Product Warranty and Returns
                </h2>
                <h3 className="text-xl font-semibold mt-6 mb-3">8.1 Manufacturer Warranty</h3>
                <p>
                  Products may be covered by manufacturer warranties. Warranty terms, duration, and coverage vary by 
                  product and manufacturer. We will assist in facilitating warranty claims but are not responsible 
                  for manufacturer warranty decisions or coverage.
                </p>
                <h3 className="text-xl font-semibold mt-6 mb-3">8.2 No Returns on Custom Orders</h3>
                <p>
                  <strong>ALL FLOORING ORDERS ARE CONSIDERED CUSTOM ORDERS AND ARE NON-RETURNABLE.</strong> Due to the 
                  nature of our cooperative purchasing model, products are ordered specifically based on customer 
                  commitments and cannot be returned for refund or exchange except in cases of:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Manufacturing defects (must be reported within 48 hours of delivery)</li>
                  <li>Shipping damage (must be documented at delivery)</li>
                  <li>Incorrect product shipped (must be reported within 48 hours)</li>
                </ul>
                <h3 className="text-xl font-semibold mt-6 mb-3">8.3 Defect Claims</h3>
                <p>
                  Claims for manufacturing defects must include photographs, product lot numbers, and detailed descriptions. 
                  We reserve the right to inspect claimed defects before approving any remedy. Industry-standard variations 
                  in color, grain pattern, and texture do not constitute defects.
                </p>
              </section>

              {/* Section 9 */}
              <section className="mb-10">
                <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-3 mb-4">
                  <span className="w-8 h-8 bg-primary text-white rounded-full flex items-center justify-center text-sm font-bold">9</span>
                  Limitation of Liability
                </h2>
                <div className="bg-red-50 border-2 border-red-200 rounded-2xl p-6">
                  <p className="text-red-900 font-semibold mb-4">
                    PLEASE READ THIS SECTION CAREFULLY AS IT LIMITS OUR LIABILITY TO YOU.
                  </p>
                  <p className="text-red-800 text-sm mb-4">
                    TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, IN NO EVENT SHALL {companyName.toUpperCase()}, 
                    ITS OFFICERS, DIRECTORS, EMPLOYEES, AGENTS, SUPPLIERS, OR LICENSORS BE LIABLE FOR ANY INDIRECT, 
                    INCIDENTAL, SPECIAL, CONSEQUENTIAL, PUNITIVE, OR EXEMPLARY DAMAGES, INCLUDING BUT NOT LIMITED TO 
                    DAMAGES FOR LOSS OF PROFITS, GOODWILL, USE, DATA, OR OTHER INTANGIBLE LOSSES, REGARDLESS OF WHETHER 
                    WE HAVE BEEN ADVISED OF THE POSSIBILITY OF SUCH DAMAGES.
                  </p>
                  <p className="text-red-800 text-sm mb-4">
                    OUR TOTAL AGGREGATE LIABILITY FOR ANY CLAIMS ARISING FROM OR RELATED TO THESE TERMS OR YOUR USE 
                    OF THE SERVICES SHALL NOT EXCEED THE AMOUNT YOU PAID TO US IN THE TWELVE (12) MONTHS PRECEDING 
                    THE CLAIM, OR ONE THOUSAND DOLLARS ($1,000), WHICHEVER IS GREATER.
                  </p>
                  <p className="text-red-800 text-sm mb-0">
                    SOME JURISDICTIONS DO NOT ALLOW THE EXCLUSION OR LIMITATION OF CERTAIN DAMAGES. IF THESE LAWS 
                    APPLY TO YOU, SOME OR ALL OF THE ABOVE EXCLUSIONS OR LIMITATIONS MAY NOT APPLY, AND YOU MAY 
                    HAVE ADDITIONAL RIGHTS.
                  </p>
                </div>
              </section>

              {/* Section 10 */}
              <section className="mb-10">
                <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-3 mb-4">
                  <span className="w-8 h-8 bg-primary text-white rounded-full flex items-center justify-center text-sm font-bold">10</span>
                  Indemnification
                </h2>
                <p>
                  You agree to indemnify, defend, and hold harmless {companyName}, its officers, directors, employees, 
                  agents, suppliers, and licensors from and against any and all claims, liabilities, damages, losses, 
                  costs, expenses, or fees (including reasonable attorneys' fees) arising from:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Your use of the Services</li>
                  <li>Your violation of these Terms</li>
                  <li>Your violation of any rights of a third party</li>
                  <li>Your violation of any applicable laws or regulations</li>
                  <li>Any content you submit or transmit through the Services</li>
                  <li>Your negligence or willful misconduct</li>
                </ul>
              </section>

              {/* Section 11 */}
              <section className="mb-10">
                <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-3 mb-4">
                  <span className="w-8 h-8 bg-primary text-white rounded-full flex items-center justify-center text-sm font-bold">11</span>
                  Dispute Resolution and Arbitration
                </h2>
                <div className="bg-blue-50 border-2 border-blue-200 rounded-2xl p-6">
                  <h3 className="text-xl font-semibold text-blue-900 mt-0 mb-3">MANDATORY ARBITRATION AGREEMENT</h3>
                  <p className="text-blue-800 text-sm mb-4">
                    <strong>PLEASE READ THIS SECTION CAREFULLY. IT AFFECTS YOUR LEGAL RIGHTS, INCLUDING YOUR RIGHT 
                    TO FILE A LAWSUIT IN COURT.</strong>
                  </p>
                  <p className="text-blue-800 text-sm mb-4">
                    You and {companyName} agree that any dispute, claim, or controversy arising out of or relating 
                    to these Terms or the breach, termination, enforcement, interpretation, or validity thereof, 
                    or to the use of the Services (collectively, "Disputes") will be resolved exclusively through 
                    final and binding arbitration, rather than in court.
                  </p>
                  <p className="text-blue-800 text-sm mb-4">
                    <strong>CLASS ACTION WAIVER:</strong> YOU AND {companyName.toUpperCase()} AGREE THAT EACH MAY BRING 
                    CLAIMS AGAINST THE OTHER ONLY IN YOUR OR ITS INDIVIDUAL CAPACITY AND NOT AS A PLAINTIFF OR CLASS 
                    MEMBER IN ANY PURPORTED CLASS OR REPRESENTATIVE PROCEEDING.
                  </p>
                  <p className="text-blue-800 text-sm mb-0">
                    The arbitration will be administered by the American Arbitration Association (AAA) under its 
                    Commercial Arbitration Rules. The arbitration will be conducted in the State of Delaware. 
                    The arbitrator's decision will be final and binding, and judgment may be entered in any court 
                    of competent jurisdiction.
                  </p>
                </div>
                <h3 className="text-xl font-semibold mt-6 mb-3">11.1 Informal Resolution</h3>
                <p>
                  Before initiating arbitration, you agree to first contact us at {companyEmail} and attempt to 
                  resolve the dispute informally. If we are unable to resolve the dispute within 60 days, either 
                  party may proceed with arbitration.
                </p>
                <h3 className="text-xl font-semibold mt-6 mb-3">11.2 Exceptions</h3>
                <p>
                  Notwithstanding the above, either party may seek injunctive or other equitable relief in any 
                  court of competent jurisdiction to prevent the actual or threatened infringement or misappropriation 
                  of intellectual property rights.
                </p>
              </section>

              {/* Section 12 */}
              <section className="mb-10">
                <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-3 mb-4">
                  <span className="w-8 h-8 bg-primary text-white rounded-full flex items-center justify-center text-sm font-bold">12</span>
                  Fraud Prevention and Security
                </h2>
                <p>
                  We take fraud seriously and employ various measures to protect our business and legitimate customers. 
                  You acknowledge and agree that:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>We may verify your identity through email verification, phone verification, or other means</li>
                  <li>We may place holds on orders pending verification for fraud prevention purposes</li>
                  <li>We may require additional documentation for large orders or orders flagged by our systems</li>
                  <li>We may cancel orders suspected of fraud without prior notice</li>
                  <li>Fraudulent orders will be reported to law enforcement and relevant authorities</li>
                  <li>We may share information with payment processors and fraud prevention services</li>
                </ul>
                <p className="mt-4">
                  <strong>UNAUTHORIZED USE OF STOLEN PAYMENT METHODS IS A FEDERAL CRIME.</strong> We actively cooperate 
                  with law enforcement in the investigation and prosecution of fraud.
                </p>
              </section>

              {/* Section 13 */}
              <section className="mb-10">
                <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-3 mb-4">
                  <span className="w-8 h-8 bg-primary text-white rounded-full flex items-center justify-center text-sm font-bold">13</span>
                  Intellectual Property
                </h2>
                <p>
                  All content on this website, including but not limited to text, graphics, logos, images, audio clips, 
                  digital downloads, data compilations, and software, is the property of {companyName} or its content 
                  suppliers and is protected by United States and international copyright, trademark, and other 
                  intellectual property laws.
                </p>
                <p>
                  You may not reproduce, distribute, modify, create derivative works of, publicly display, publicly 
                  perform, republish, download, store, or transmit any content on our website without our prior 
                  written consent.
                </p>
              </section>

              {/* Section 14 */}
              <section className="mb-10">
                <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-3 mb-4">
                  <span className="w-8 h-8 bg-primary text-white rounded-full flex items-center justify-center text-sm font-bold">14</span>
                  Force Majeure
                </h2>
                <p>
                  We shall not be liable for any failure or delay in performance due to circumstances beyond our 
                  reasonable control, including but not limited to:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Acts of God, natural disasters, or severe weather</li>
                  <li>War, terrorism, civil unrest, or government actions</li>
                  <li>Pandemics, epidemics, or public health emergencies</li>
                  <li>Labor disputes, strikes, or shortages</li>
                  <li>Supply chain disruptions or material shortages</li>
                  <li>Shipping delays, port congestion, or carrier issues</li>
                  <li>Power outages, internet failures, or system failures</li>
                  <li>Changes in laws or regulations</li>
                </ul>
                <p className="mt-4">
                  In such events, our obligations will be suspended for the duration of the force majeure event. 
                  We will make reasonable efforts to resume performance as soon as practicable.
                </p>
              </section>

              {/* Section 15 */}
              <section className="mb-10">
                <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-3 mb-4">
                  <span className="w-8 h-8 bg-primary text-white rounded-full flex items-center justify-center text-sm font-bold">15</span>
                  Governing Law
                </h2>
                <p>
                  These Terms shall be governed by and construed in accordance with the laws of the State of Delaware, 
                  United States, without regard to its conflict of law provisions. Any legal action or proceeding 
                  not subject to arbitration shall be brought exclusively in the state or federal courts located 
                  in Delaware, and you consent to the personal jurisdiction of such courts.
                </p>
              </section>

              {/* Section 16 */}
              <section className="mb-10">
                <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-3 mb-4">
                  <span className="w-8 h-8 bg-primary text-white rounded-full flex items-center justify-center text-sm font-bold">16</span>
                  Modifications to Terms
                </h2>
                <p>
                  We reserve the right to modify these Terms at any time at our sole discretion. Changes will be 
                  effective immediately upon posting to this website. Your continued use of the Services after 
                  any changes constitutes your acceptance of the new Terms. It is your responsibility to review 
                  these Terms periodically for changes.
                </p>
                <p>
                  For material changes, we may provide additional notice via email or prominent website posting. 
                  However, it is your responsibility to ensure your email address is current and to check for updates.
                </p>
              </section>

              {/* Section 17 */}
              <section className="mb-10">
                <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-3 mb-4">
                  <span className="w-8 h-8 bg-primary text-white rounded-full flex items-center justify-center text-sm font-bold">17</span>
                  Severability
                </h2>
                <p>
                  If any provision of these Terms is held to be invalid, illegal, or unenforceable by a court of 
                  competent jurisdiction, such provision shall be modified to the minimum extent necessary to make 
                  it valid and enforceable, or if modification is not possible, severed from these Terms. The 
                  remaining provisions shall continue in full force and effect.
                </p>
              </section>

              {/* Section 18 */}
              <section className="mb-10">
                <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-3 mb-4">
                  <span className="w-8 h-8 bg-primary text-white rounded-full flex items-center justify-center text-sm font-bold">18</span>
                  Entire Agreement
                </h2>
                <p>
                  These Terms, together with our Payment Policy and Privacy Policy, constitute the entire agreement 
                  between you and {companyName} regarding your use of the Services and supersede all prior and 
                  contemporaneous agreements, proposals, or representations, written or oral, concerning the subject 
                  matter hereof.
                </p>
                <p>
                  No waiver of any term of these Terms shall be deemed a further or continuing waiver of such term 
                  or any other term, and our failure to assert any right or provision under these Terms shall not 
                  constitute a waiver of such right or provision.
                </p>
              </section>

              {/* Section 19 */}
              <section className="mb-10">
                <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-3 mb-4">
                  <span className="w-8 h-8 bg-primary text-white rounded-full flex items-center justify-center text-sm font-bold">19</span>
                  Contact Information
                </h2>
                <p>
                  If you have any questions about these Terms, please contact us:
                </p>
                <div className="bg-gray-50 rounded-xl p-6 mt-4">
                  <p className="font-bold text-lg mb-4">{companyName}</p>
                  <div className="space-y-2 text-gray-700">
                    <p className="flex items-center gap-3">
                      <Mail className="w-5 h-5 text-primary" />
                      <span>Email: {companyEmail}</span>
                    </p>
                    <p className="flex items-center gap-3">
                      <Phone className="w-5 h-5 text-primary" />
                      <span>Phone: {companyPhone}</span>
                    </p>
                    <p className="flex items-center gap-3">
                      <Globe className="w-5 h-5 text-primary" />
                      <span>Address: {companyAddress}</span>
                    </p>
                  </div>
                </div>
              </section>

              {/* Final Acknowledgment */}
              <div className="bg-primary/5 border-2 border-primary/20 rounded-2xl p-6 mt-10">
                <p className="text-gray-800 font-semibold mb-2">
                  BY USING OUR SERVICES OR PLACING AN ORDER, YOU ACKNOWLEDGE THAT:
                </p>
                <ul className="text-gray-700 space-y-2">
                  <li>1. You have read and understood these Terms of Service</li>
                  <li>2. You agree to be legally bound by these Terms</li>
                  <li>3. You are at least 18 years of age</li>
                  <li>4. You have the authority to enter into this agreement</li>
                </ul>
              </div>

            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Terms;
